import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { X, Printer, Save, FileText, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LETTER_STATUSES = ['Draft', 'Final', 'Printed', 'Sent'];
const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];

export default function LetterEditor({ 
  isOpen, 
  onClose, 
  letter, 
  templates, 
  client, 
  onSave,
  onRegenerate,
  isNew = false 
}) {
  const [title, setTitle] = useState(letter?.title || '');
  const [content, setContent] = useState(letter?.content || '');
  const [letterType, setLetterType] = useState(letter?.letter_type || '');
  const [status, setStatus] = useState(letter?.status || 'Draft');
  const [bureau, setBureau] = useState(letter?.bureau || '');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const printRef = useRef(null);

  // Replace placeholders with client data
  const replacePlaceholders = (text) => {
    if (!text || !client) return text;
    
    const replacements = {
      '[Your Full Name]': `${client.first_name} ${client.last_name}`,
      '[Your Name]': `${client.first_name} ${client.last_name}`,
      '[Your Address]': client.address || '[Your Address]',
      '[City, State, ZIP Code]': client.address || '[City, State, ZIP Code]',
      '[Email Address]': client.email || '[Email Address]',
      '[Phone Number]': client.phone || '[Phone Number]',
      '[Date]': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      '[Your Printed Name]': `${client.first_name} ${client.last_name}`,
      '[Printed Name]': `${client.first_name} ${client.last_name}`,
      '[Your Date of Birth]': client.dob || '[Your Date of Birth]',
      '[MM/DD/YYYY]': client.dob || '[MM/DD/YYYY]',
      '[Social Security Number: XXX-XX-XXXX]': client.ssn_masked || '***-**-****',
      '[XXX-XX-XXXX]': client.ssn_masked || '***-**-****',
    };

    let result = text;
    for (const [placeholder, value] of Object.entries(replacements)) {
      result = result.split(placeholder).join(value);
    }
    return result;
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setTitle(template.name);
      setLetterType(template.category);
      setContent(replacePlaceholders(template.content));
    }
  };

  const handleSave = () => {
    onSave({
      id: letter?.id,
      title,
      content,
      letter_type: letterType,
      status,
      bureau: bureau || null,
      template_id: selectedTemplateId || letter?.template_id
    });
  };

  const handlePrint = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/letters/generate-pdf`, {
        content: content,
        title: title || 'Letter'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(title || 'Letter').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Update status to Printed if not already Sent
      if (status !== 'Sent') {
        setStatus('Printed');
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF');
    }
  };

  const handleRegenerate = () => {
    if (onRegenerate && letter?.id) {
      onRegenerate(letter);
    }
  };

  const statusColors = {
    Draft: 'bg-slate-100 text-slate-800',
    Final: 'bg-blue-100 text-blue-800',
    Printed: 'bg-emerald-100 text-emerald-800',
    Sent: 'bg-purple-100 text-purple-800'
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl">
                {isNew ? 'Create New Letter' : 'Edit Letter'}
              </DialogTitle>
              {!isNew && letter?.is_generated && (
                <Badge variant="outline" className="text-xs">Auto-Generated</Badge>
              )}
              {letter?.bureau && (
                <Badge className="bg-blue-100 text-blue-800 text-xs">{letter.bureau}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isNew && letter?.is_generated && onRegenerate && (
                <Button variant="outline" size="sm" onClick={handleRegenerate} data-testid="regenerate-letter-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-letter-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button size="sm" onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-letter-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-6 p-4">
            {/* Left side - Form controls */}
            <div className="space-y-4">
              {isNew && templates && templates.length > 0 && (
                <div>
                  <Label>Select Template</Label>
                  <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
                    <SelectTrigger data-testid="select-letter-template">
                      <SelectValue placeholder="Choose a template..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} ({t.category})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="letter-title">Letter Title</Label>
                <Input
                  id="letter-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter letter title"
                  data-testid="input-letter-title"
                />
              </div>

              <div>
                <Label htmlFor="letter-type">Letter Type</Label>
                <Input
                  id="letter-type"
                  value={letterType}
                  onChange={(e) => setLetterType(e.target.value)}
                  placeholder="e.g., Dispute, Goodwill, Request"
                  data-testid="input-letter-type"
                />
              </div>

              <div>
                <Label>Credit Bureau</Label>
                <Select value={bureau} onValueChange={setBureau}>
                  <SelectTrigger data-testid="select-bureau">
                    <SelectValue placeholder="Select bureau (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {BUREAUS.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="select-letter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LETTER_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${statusColors[s]?.split(' ')[0]}`} />
                          {s}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t">
                <p className="text-xs text-slate-500 mb-2">Client Info:</p>
                <div className="text-sm space-y-1 text-slate-600">
                  <p><strong>Name:</strong> {client?.first_name} {client?.last_name}</p>
                  <p><strong>Email:</strong> {client?.email}</p>
                  <p><strong>Phone:</strong> {client?.phone}</p>
                  <p><strong>Address:</strong> {client?.address || 'Not provided'}</p>
                </div>
              </div>

              {letter?.account_reference && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-1">Account Reference:</p>
                  <p className="text-sm text-slate-700">{letter.account_reference}</p>
                </div>
              )}
            </div>

            {/* Right side - Letter content editor */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="letter-content">Letter Content</Label>
                <span className="text-xs text-slate-400">Plain text - ready for printing</span>
              </div>
              <Textarea
                id="letter-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[500px] font-mono text-sm bg-white border-slate-300"
                placeholder="Enter letter content..."
                style={{ 
                  fontFamily: "'Times New Roman', Times, serif",
                  lineHeight: '1.6'
                }}
                data-testid="textarea-letter-content"
              />
            </div>
          </div>

          {/* Print Preview (hidden, used for printing) */}
          <div ref={printRef} className="hidden print:block">
            <div style={{ 
              fontFamily: "'Times New Roman', Times, serif",
              fontSize: '12pt',
              lineHeight: '1.6',
              color: '#000',
              background: '#fff',
              whiteSpace: 'pre-wrap'
            }}>
              {content}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-letter-final">
            Save Letter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
